import { State } from 'app/gamma/src/modules/types';
import {
  cancelAlert,
  raiseAlert,
  AlertArea,
  AlertName,
} from 'app/gamma/src/modules/state/ui/alerts';
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Transition } from 'react-transition-group';
import { isAlertShown } from 'app/gamma/src/selectors/alerts';
import { Store } from 'redux';
import { LayerManagerPortal, Layers } from 'app/src/components/LayerManager';
import Alert, { AlertProps } from './alert';
import { defaultStore } from '../../defaultStore';
import { Dispatch } from 'app/gamma/src/types';

interface DispatchProps {
  removeAlert: (alertArea: string) => void;
}

interface StateProps {
  isActive: boolean;
}

interface AllProps extends DispatchProps, StateProps, AlertProps {}

export class AlertContainerUnconnected extends React.Component<AllProps> {
  defaultTimeout = 5000;

  render() {
    const { children, isActive, removeAlert, ...rest } = this.props;

    return (
      <LayerManagerPortal layer={Layers.Alert}>
        <Transition
          appear
          in={isActive}
          onEntered={() => removeAlert(this.props.area)}
          timeout={this.props.msTimeout || this.defaultTimeout}
        >
          {(state) => {
            if (state === 'entering') {
              return <Alert {...rest}>{children}</Alert>;
            } else {
              return '';
            }
          }}
        </Transition>
      </LayerManagerPortal>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    removeAlert(alertArea) {
      dispatch(cancelAlert({ alertArea }));
    },
  };
};

const mapStateToProps = (state: State, ownProps: AlertProps): StateProps => {
  return {
    isActive: isAlertShown(state, ownProps.area, ownProps.name),
  };
};

/**
 * Deprecating in favor of showFlag. If you need to implement a custom alert
 * during this migration, please reach out to @raymond.
 * @deprecated
 */
export const AlertContainer: React.ComponentType<AlertProps> = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AlertContainerUnconnected);

interface UseAlertArgs {
  alertArea: AlertArea;
  alertName: AlertName;
  reduxStore?: Store<State>;
}

interface UseAlertReturn {
  isAlertShown: boolean;
  showAlert: () => void;
  closeAlert: () => void;
}

/**
 * Deprecating in favor of showFlag. If you need to implement a custom alert
 * during this migration, please reach out to @raymond.
 * @deprecated
 */
export const useAlert = ({
  alertArea,
  alertName,
  reduxStore,
}: UseAlertArgs): UseAlertReturn => {
  const store = reduxStore || defaultStore;
  const [isAlertOpen, setIsAlertOpen] = useState(
    isAlertShown(store.getState(), alertArea, alertName),
  );

  useEffect(() => {
    const storeSubscription = store.subscribe(() => {
      const state = store.getState();
      setIsAlertOpen(isAlertShown(state, alertArea, alertName));
    });

    return storeSubscription;
  }, [store, alertArea, alertName]);

  const onShowAlert = useCallback(() => {
    store.dispatch(raiseAlert({ alertArea, alertName }));
  }, [store, alertArea, alertName]);

  const onCloseAlert = useCallback(() => {
    store.dispatch(cancelAlert({ alertArea }));
  }, [store, alertArea]);

  return {
    isAlertShown: isAlertOpen,
    showAlert: onShowAlert,
    closeAlert: onCloseAlert,
  };
};
